/** W82 Eine-Frage-Regel (plan-w82.md 3.5): jede Stelle fragt `darf(anlass, …)`, keine fragt die Stufe selbst ab.
 *  `erklaert` sagt der Aufrufer (Überfälliges, wartender Vorschlag, Fehler oder erste Nutzung). Dieselbe Tabelle steht
 *  in iOS `WeltDarf.swift` und in beiden Tests als Literal. */
import type { Fassung, Praesenz } from "./einstellungen";

export type Anlass =
  | "vorhang"
  | "rundflug"
  | "lichtKachel"
  | "satzKopf"
  | "zeigen"
  | "flugTipp"
  | "satzAntwort"
  | "flugAntwort"
  | "scheinwerfer"
  | "lichtspur"
  | "geste"
  | "satzAkten"
  | "flugAkten"
  | "randgeist"
  | "fehler"
  | "einfuehrung";

/** ja, nein oder e (nur wenn `erklaert`) */
type Wert = "ja" | "nein" | "e";

/** Spalten: lebhaft Überall, Erklären, Keine; ruhig Überall, Erklären, Keine */
const TABELLE: Record<Anlass, readonly [Wert, Wert, Wert, Wert, Wert, Wert]> = {
  vorhang: ["ja", "nein", "nein", "nein", "nein", "nein"],
  rundflug: ["ja", "nein", "nein", "nein", "nein", "nein"],
  lichtKachel: ["ja", "e", "nein", "nein", "nein", "nein"],
  satzKopf: ["ja", "e", "nein", "ja", "e", "nein"],
  zeigen: ["ja", "ja", "nein", "ja", "ja", "nein"],
  flugTipp: ["ja", "e", "nein", "e", "nein", "nein"],
  satzAntwort: ["ja", "e", "nein", "ja", "e", "nein"],
  flugAntwort: ["ja", "nein", "nein", "nein", "nein", "nein"],
  scheinwerfer: ["ja", "nein", "nein", "nein", "nein", "nein"],
  lichtspur: ["ja", "ja", "nein", "nein", "nein", "nein"],
  geste: ["ja", "e", "nein", "ja", "e", "nein"],
  satzAkten: ["ja", "e", "nein", "ja", "e", "nein"],
  flugAkten: ["ja", "nein", "nein", "e", "nein", "nein"],
  randgeist: ["nein", "nein", "nein", "ja", "e", "nein"],
  fehler: ["ja", "ja", "nein", "ja", "ja", "nein"],
  einfuehrung: ["ja", "ja", "ja", "ja", "ja", "ja"],
};

const STUFE: Record<Praesenz, number> = { ueberall: 0, erklaeren: 1, keine: 2 };

export function darf(anlass: Anlass, lage: { fassung: Fassung; praesenz: Praesenz; erklaert: boolean }): boolean {
  const wert = TABELLE[anlass][(lage.fassung === "ruhig" ? 3 : 0) + STUFE[lage.praesenz]];
  return wert === "ja" || (wert === "e" && lage.erklaert);
}

/** Leben auf seinem Platz (Atmen und Blinzeln immer, außer bei reduzierter Bewegung); Schweben in px, Neigung in Grad. */
export type Leben = {
  atmen: boolean;
  blinzeln: boolean;
  blick: boolean;
  funkeln: boolean;
  teilchen: boolean;
  schweben: number;
  neigung: number;
};

export function leben(fassung: Fassung, praesenz: Praesenz, reduziert = false): Leben {
  const still: Leben = { atmen: false, blinzeln: false, blick: false, funkeln: false, teilchen: false, schweben: 0, neigung: 0 };
  if (reduziert) return still;
  const grund = { ...still, atmen: true, blinzeln: true };
  if (fassung === "lebhaft") {
    return praesenz === "ueberall" ? { ...grund, blick: true, funkeln: true, teilchen: true, schweben: 7, neigung: 1.5 } : grund;
  }
  if (praesenz === "ueberall") return { ...grund, blick: true, funkeln: true, schweben: 5, neigung: 1.2 };
  // W9: Blick und Dauerfunkeln in den leisen Stufen aus; Schweben bleibt Sache der Fassung
  return praesenz === "erklaeren" ? { ...grund, schweben: 3, neigung: 0.8 } : { ...grund, schweben: 2, neigung: 0 };
}

export type Stopp = "ueberfaellig" | "bald" | "vorschlag";

/** Stopps des Knopfs „Zeig …“: lebhaft Überall drei, lebhaft Erklären zwei, ruhig Überall und Erklären eine Stelle
 *  (Überfälliges, sonst Vorschlag), Keine Erklärungen keine. Stopps ohne Ziel fallen weg; ohne Ziel kein Knopf. */
export function zeigenStopps(fassung: Fassung, praesenz: Praesenz, ziele: Record<Stopp, boolean> = { ueberfaellig: true, bald: true, vorschlag: true }): Stopp[] {
  if (praesenz === "keine") return [];
  const mitZiel = (s: Stopp[]) => s.filter((x) => ziele[x]);
  if (fassung === "ruhig") return mitZiel(["ueberfaellig", "vorschlag"]).slice(0, 1);
  return mitZiel(praesenz === "ueberall" ? ["ueberfaellig", "bald", "vorschlag"] : ["ueberfaellig", "vorschlag"]);
}
